const runtimeListeners = new Set();

let installedRuntimeHandlers = false;
let lastRuntimeError = null;

export const isChunkLoadError = (error) => {
  const text = `${error?.name || ''} ${error?.message || ''}`.toLowerCase();
  return (
    text.includes('chunkloaderror') ||
    text.includes('loading chunk') ||
    text.includes('failed to fetch dynamically imported module') ||
    text.includes('importing a module script failed') ||
    text.includes('dynamically imported module')
  );
};

const inferAppErrorKind = (error) => {
  if (isChunkLoadError(error)) {
    return 'chunk_load';
  }
  if (error?.code === 'ECONNABORTED') {
    return 'timeout';
  }
  const text = `${error?.message || ''}`.toLowerCase();
  if (text.includes('timeout') || text.includes('超时')) {
    return 'timeout';
  }
  if (error?.name === 'ResourceLoadError') {
    return 'resource_load';
  }
  if (error?.name === 'UnhandledRejectionError') {
    return 'unhandled_rejection';
  }
  return 'runtime';
};

export const normalizeAppError = (input, context = {}) => {
  const error =
    input instanceof Error
      ? input
      : new Error(
          String(
            input?.message || context.message || '控制台加载时发生未知异常'
          )
        );

  error.appKind = context.kind || error.appKind || inferAppErrorKind(error);
  error.appStage = context.stage || error.appStage || '';
  error.appPath =
    context.path ||
    error.appPath ||
    (typeof window !== 'undefined' ? window.location.pathname : '');
  error.appEndpoint = context.endpoint || error.appEndpoint || '';
  error.appDisplayMessage =
    context.displayMessage ||
    error.appDisplayMessage ||
    String(error.message || context.message || '控制台加载时发生未知异常');
  error.appDetail =
    context.detail || error.appDetail || String(error.message || '').trim();
  error.appComponentStack =
    context.componentStack || error.appComponentStack || '';
  error.appResource =
    context.resource || error.appResource || context.filename || '';
  error.appScope = context.scope || error.appScope || '';
  return error;
};

export const reportAppRuntimeError = (input, context = {}) => {
  const normalized = normalizeAppError(input, context);
  lastRuntimeError = normalized;
  console.error('[app-runtime-error]', normalized);
  runtimeListeners.forEach((listener) => listener(lastRuntimeError));
  return normalized;
};

export const clearAppRuntimeError = () => {
  lastRuntimeError = null;
  runtimeListeners.forEach((listener) => listener(null));
};

export const getAppRuntimeError = () => lastRuntimeError;

export const subscribeAppRuntimeError = (listener) => {
  runtimeListeners.add(listener);
  return () => {
    runtimeListeners.delete(listener);
  };
};

const handleWindowError = (event) => {
  if (event?.error) {
    reportAppRuntimeError(event.error, {
      stage: 'window_error',
      filename: event.filename,
    });
    return;
  }

  const target = event?.target;
  if (target && target !== window) {
    const tagName = String(target.tagName || '').toUpperCase();
    if (tagName !== 'SCRIPT' && tagName !== 'LINK') {
      return;
    }
    const resource =
      target.src || target.href || target.currentSrc || target.baseURI || '';
    const resourceError = new Error(
      resource ? `资源加载失败：${resource}` : '资源加载失败'
    );
    resourceError.name = 'ResourceLoadError';
    reportAppRuntimeError(resourceError, {
      stage: 'resource_load',
      resource,
    });
    return;
  }

  if (event?.message) {
    reportAppRuntimeError(new Error(event.message), {
      stage: 'window_error',
      filename: event.filename,
    });
  }
};

const handleUnhandledRejection = (event) => {
  const reason =
    event?.reason instanceof Error
      ? event.reason
      : new Error(
          String(event?.reason?.message || event?.reason || '未处理的异步异常')
        );
  reason.name = reason.name || 'UnhandledRejectionError';
  reportAppRuntimeError(reason, {
    stage: 'unhandled_rejection',
  });
};

export const installGlobalAppErrorHandlers = () => {
  if (installedRuntimeHandlers || typeof window === 'undefined') {
    return;
  }
  installedRuntimeHandlers = true;
  window.addEventListener('error', handleWindowError, true);
  window.addEventListener('unhandledrejection', handleUnhandledRejection);
};
